import React, { useEffect, useState } from "react";
import { apiFetchJson } from "../api";
import { t, tf, locale, useLang } from "../i18n";
import { confirmAction } from "../utils/confirmDialog";
import { GiftIcon, ChevronRightIcon, RefreshIcon, CopyIcon, CheckIcon, TrashIcon, XIcon, Spinner } from "./Icons";

// Ключи подписки — в администрировании (только для администратора). Ключ
// выпускается на срок в днях, его можно разыграть или подарить, а человек
// активирует его сам, когда ему нужно: в настройках или на экране «Подписка
// не активна». Сервер — /api/admin/redeem-keys (backend/src/redeemKeyStore.js).
// Свёрнуто по умолчанию; сколько ключей свободно, видно и в заголовке.
const OPEN_STORAGE_KEY = "kl_admin_redeem_keys_open";
const FILTER_STORAGE_KEY = "kl_admin_redeem_keys_filter";
const FILTERS = [
  { value: "free", label: "Свободные" },
  { value: "redeemed", label: "Активированные" },
  { value: "all", label: "Все" }
];
const DAY_PRESETS = [1, 3, 7, 14, 30];
const VALIDITY_PRESETS = [
  { value: 0, label: "Без срока" },
  { value: 7, label: "7 дней" },
  { value: 30, label: "30 дней" },
  { value: 90, label: "90 дней" }
];
const MAX_BATCH = 50;

const DAY_FORMS_RU = ["день", "дня", "дней"];
const daysLabel = (lang, days) => {
  const value = Math.max(0, Math.round(Number(days) || 0));
  if (lang === "en") return `${value} ${value === 1 ? "day" : "days"}`;
  const mod10 = value % 10;
  const mod100 = value % 100;
  const form = mod10 === 1 && mod100 !== 11
    ? DAY_FORMS_RU[0]
    : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? DAY_FORMS_RU[1] : DAY_FORMS_RU[2]);
  return `${value} ${form}`;
};

const formatDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" })
  : "");
const formatDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString(locale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })
  : "");

const readStored = (key, fallback, allowed) => {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const writeClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const AdminRedeemKeysSection = ({ isPhoneView = false, onError, onNotify }) => {
  const lang = useLang();
  const [open, setOpen] = useState(() => readStored(OPEN_STORAGE_KEY, "0", ["0", "1"]) === "1");
  const [filter, setFilter] = useState(() => readStored(FILTER_STORAGE_KEY, "free", FILTERS.map((item) => item.value)));
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ days: "7", count: "1", validDays: 0, note: "" });
  const [creating, setCreating] = useState(false);
  // Только что выпущенные — отдельной плашкой сверху, чтобы скопировать разом.
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState("");
  const [deleting, setDeleting] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiFetchJson("/api/admin/redeem-keys", { timeoutMs: 20000, retry: true });
      if (response?.success) setItems(Array.isArray(response.items) ? response.items : []);
    } catch (error) {
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  // Число свободных — в заголовке, даже когда раздел свёрнут.
  useEffect(() => {
    load();
    // Только при входе в раздел настроек.
  }, []);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(OPEN_STORAGE_KEY, next ? "1" : "0"); } catch { /* приватное окно */ }
      return next;
    });
  };
  const changeFilter = (value) => {
    setFilter(value);
    try { localStorage.setItem(FILTER_STORAGE_KEY, value); } catch { /* приватное окно */ }
  };

  const flashCopied = (id) => {
    setCopied(id);
    window.setTimeout(() => setCopied((current) => (current === id ? "" : current)), 1400);
  };
  const copyText = async (text, id) => {
    if (await writeClipboard(text)) flashCopied(id);
    else onNotify?.({ variant: "warning", title: t("Не скопировалось"), body: t("Буфер обмена недоступен — выделите ключ и скопируйте вручную.") });
  };

  const days = Math.round(Number(form.days));
  const count = Math.round(Number(form.count));
  const formValid = Number.isFinite(days) && days >= 1 && days <= 365
    && Number.isFinite(count) && count >= 1 && count <= MAX_BATCH;

  const create = async (event) => {
    event.preventDefault();
    if (creating || !formValid) return;
    setCreating(true);
    try {
      const response = await apiFetchJson("/api/admin/redeem-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days, count, validDays: form.validDays, note: form.note }),
        timeoutMs: 20000
      });
      const created = Array.isArray(response?.items) ? response.items : [];
      setItems((prev) => [...created, ...(prev || [])]);
      setFresh({ items: created, days });
      setForm((prev) => ({ ...prev, note: "" }));
      if (filter === "redeemed") changeFilter("free");
    } catch (error) {
      onError?.(error);
      onNotify?.({ variant: "warning", title: t("Ошибка"), body: error?.message || t("Не удалось выпустить ключи") });
    } finally {
      setCreating(false);
    }
  };

  const remove = async (item) => {
    const used = item.status === "redeemed";
    const confirmed = await confirmAction({
      title: tf`Удалить ключ ${item.code}?`,
      message: used
        ? t("Пропадёт только запись в списке — дни, которые ключ уже добавил, у пользователя останутся.")
        : t("Ключ перестанет активироваться. Если его уже кому-то отдали, он не сработает."),
      confirmText: t("Удалить"),
      variant: "danger",
      icon: "trash"
    });
    if (!confirmed) return;
    setDeleting(item.code);
    try {
      await apiFetchJson(`/api/admin/redeem-keys/${encodeURIComponent(item.code)}`, { method: "DELETE", timeoutMs: 20000 });
      setItems((prev) => (prev || []).filter((entry) => entry.code !== item.code));
      setFresh((prev) => (prev ? { ...prev, items: prev.items.filter((entry) => entry.code !== item.code) } : prev));
    } catch (error) {
      onError?.(error);
      onNotify?.({ variant: "error", title: t("Ошибка"), body: error?.message || t("Не удалось удалить ключ") });
    } finally {
      setDeleting("");
    }
  };

  const list = items || [];
  const freeCount = list.filter((item) => item.status === "free").length;
  const redeemedCount = list.filter((item) => item.status === "redeemed").length;
  const shown = list.filter((item) => (filter === "all"
    ? true
    : filter === "free" ? item.status !== "redeemed" : item.status === "redeemed"));
  const freshItems = fresh?.items || [];

  return (
    <div className="adm-tickets adm-keys" data-phone={isPhoneView ? "1" : "0"}>
      <button type="button" className="adm-tickets-toggle kl-plain-button" onClick={toggleOpen} aria-expanded={open}>
        <GiftIcon size={16} />
        <span className="adm-tickets-toggle-title">
          {t("Ключи подписки")}
          {items && <span className="adm-tickets-toggle-sub">{tf` · свободных ${freeCount}`}</span>}
        </span>
        <span className="adm-tickets-toggle-action">{open ? t("Скрыть") : t("Показать")}</span>
        <ChevronRightIcon size={16} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }} />
      </button>

      {open && (
        <div className="adm-tickets-body">
          <form className="adm-keys-form" onSubmit={create}>
            <div className="adm-keys-form-title">{t("Выпустить ключи")}</div>
            <div className="adm-keys-field">
              <span className="adm-keys-label">{t("Срок подписки")}</span>
              <div className="adm-keys-days">
                <div className="adm-tickets-filters adm-keys-chips" role="group" aria-label={t("Срок подписки")}>
                  {DAY_PRESETS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="adm-tickets-filter"
                      aria-pressed={days === value}
                      onClick={() => setForm((prev) => ({ ...prev, days: String(value) }))}
                    >
                      {daysLabel(lang, value)}
                    </button>
                  ))}
                </div>
                <label className="adm-keys-custom">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    inputMode="numeric"
                    value={form.days}
                    onChange={(event) => setForm((prev) => ({ ...prev, days: event.target.value }))}
                    aria-label={t("Своё число дней")}
                  />
                  <span>{t("дн.")}</span>
                </label>
              </div>
            </div>
            <div className="adm-keys-row">
              <label className="adm-keys-field adm-keys-count">
                <span className="adm-keys-label">{t("Сколько ключей")}</span>
                <input
                  type="number"
                  min="1"
                  max={MAX_BATCH}
                  inputMode="numeric"
                  value={form.count}
                  onChange={(event) => setForm((prev) => ({ ...prev, count: event.target.value }))}
                />
              </label>
              <div className="adm-keys-field">
                <span className="adm-keys-label">{t("Активировать в течение")}</span>
                <div className="adm-tickets-filters adm-keys-chips" role="group" aria-label={t("Активировать в течение")}>
                  {VALIDITY_PRESETS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className="adm-tickets-filter"
                      aria-pressed={form.validDays === option.value}
                      onClick={() => setForm((prev) => ({ ...prev, validDays: option.value }))}
                    >
                      {t(option.label)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <label className="adm-keys-field">
              <span className="adm-keys-label">{t("Для чего")}</span>
              <input
                type="text"
                maxLength={120}
                value={form.note}
                onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
                placeholder={t("Например: розыгрыш в канале")}
              />
            </label>
            <div className="adm-keys-form-foot">
              <span className="adm-keys-hint">
                {formValid
                  ? tf`${count} × ${daysLabel(lang, days)} — дни добавятся к подписке того, кто активирует ключ`
                  : tf`Срок — от 1 до 365 дней, за раз — до ${MAX_BATCH} ключей`}
              </span>
              <button type="submit" className="adm-keys-submit" disabled={creating || !formValid}>
                {creating ? <Spinner size={14} /> : <GiftIcon size={15} />}
                {t("Выпустить")}
              </button>
            </div>
          </form>

          {freshItems.length > 0 && (
            <div className="adm-keys-fresh">
              <div className="adm-keys-fresh-head">
                <span>
                  <b>{tf`Готово: ${freshItems.length}`}</b>
                  {tf` · по ${daysLabel(lang, fresh.days)}`}
                </span>
                <button
                  type="button"
                  className="adm-ticket-action kl-plain-button"
                  onClick={() => copyText(freshItems.map((item) => item.code).join("\n"), "fresh-all")}
                >
                  {copied === "fresh-all" ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
                  {copied === "fresh-all" ? t("Скопировано") : (freshItems.length > 1 ? t("Скопировать все") : t("Скопировать"))}
                </button>
                <button
                  type="button"
                  className="adm-keys-icon kl-plain-button"
                  onClick={() => setFresh(null)}
                  aria-label={t("Закрыть")}
                  title={t("Закрыть")}
                >
                  <XIcon size={14} />
                </button>
              </div>
              <div className="adm-keys-fresh-codes">
                {freshItems.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className="adm-keys-code-chip"
                    onClick={() => copyText(item.code, `chip-${item.code}`)}
                    title={t("Скопировать")}
                  >
                    <code>{item.code}</code>
                    {copied === `chip-${item.code}` ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="adm-tickets-bar">
            <div className="adm-tickets-filters" role="tablist">
              {FILTERS.map((item) => {
                const amount = item.value === "free" ? freeCount : item.value === "redeemed" ? redeemedCount : list.length;
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="tab"
                    className="adm-tickets-filter"
                    aria-pressed={filter === item.value}
                    onClick={() => changeFilter(item.value)}
                  >
                    {t(item.label)}
                    {items && <span className="adm-keys-filter-count">{amount}</span>}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="adm-tickets-refresh kl-plain-button"
              onClick={load}
              disabled={loading}
              aria-label={t("Обновить")}
              title={t("Обновить")}
            >
              {loading ? <Spinner size={14} /> : <RefreshIcon size={14} />}
            </button>
          </div>

          {!items ? (
            <div className="adm-tickets-empty">{loading ? t("Загрузка...") : t("Не загрузилось — нажмите «Обновить»")}</div>
          ) : shown.length === 0 ? (
            <div className="adm-tickets-empty">
              {filter === "redeemed" ? t("Активированных ключей пока нет") : t("Свободных ключей нет — выпустите новые")}
            </div>
          ) : shown.map((item) => {
            const meta = item.status === "redeemed"
              ? tf`активировал ${item.redeemedBy?.username || item.redeemedBy?.ownerId || "—"} · ${formatDateTime(item.redeemedAt)}`
              : item.status === "expired"
                ? tf`истёк ${formatDate(item.expiresAt)}`
                : item.expiresAt
                  ? tf`активировать до ${formatDate(item.expiresAt)}`
                  : t("без срока активации");
            return (
              <div key={item.code} className="adm-key" data-status={item.status}>
                <div className="adm-key-main">
                  <div className="adm-key-head">
                    <button
                      type="button"
                      className="adm-key-code"
                      onClick={() => copyText(item.code, item.code)}
                      title={t("Скопировать")}
                      disabled={item.status !== "free"}
                    >
                      <code>{item.code}</code>
                      {item.status === "free" && (copied === item.code ? <CheckIcon size={13} /> : <CopyIcon size={13} />)}
                    </button>
                    <span className="adm-key-days">{daysLabel(lang, item.days)}</span>
                    {item.status === "redeemed" && <span className="adm-key-state is-used">{t("активирован")}</span>}
                    {item.status === "expired" && <span className="adm-key-state is-expired">{t("истёк")}</span>}
                  </div>
                  <div className="adm-key-meta">
                    {[item.note, meta, tf`выпущен ${formatDate(item.createdAt)}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button
                  type="button"
                  className="adm-keys-icon is-danger kl-plain-button"
                  onClick={() => remove(item)}
                  disabled={deleting === item.code}
                  aria-label={t("Удалить ключ")}
                  title={t("Удалить ключ")}
                >
                  {deleting === item.code ? <Spinner size={13} /> : <TrashIcon size={14} />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminRedeemKeysSection;
